/**
 * Luật thuần của form hồ sơ tổ chức: kiểm ảnh, kiểm tên, so thay đổi.
 * Server kiểm lại tất cả; kiểm sớm ở đây để báo lỗi cạnh ô trước khi gửi.
 */
import type { KhoaDich } from '../../../i18n/types';
import {
  LOGO_KIEU_CHO_PHEP,
  LOGO_TOI_DA_BYTE,
  TEN_TOI_DA,
  VIET_TAT_TOI_DA,
} from '../constants.ts';
import type { TenantProfile, TenantProfileChange, TruongDoi } from '../types.ts';

/** `null` là hợp lệ. Định dạng xét trước dung lượng: sai loại thì nén nhỏ cũng vô ích. */
export function kiemTepLogo(tep: { type: string; size: number }): KhoaDich | null {
  if (!LOGO_KIEU_CHO_PHEP.includes(tep.type)) return 'hoSoToChuc.loi.tepDinhDang';
  if (tep.size > LOGO_TOI_DA_BYTE) return 'hoSoToChuc.loi.tepQuaLon';
  return null;
}

export interface FormHoSo {
  display_name: string;
  short_name: string;
}

export type LoiFormHoSo = Partial<Record<keyof FormHoSo, KhoaDich>>;

export function kiemForm(form: FormHoSo): LoiFormHoSo {
  const loi: LoiFormHoSo = {};
  if (form.display_name.trim() === '') loi.display_name = 'hoSoToChuc.loi.tenRong';
  else if (form.display_name.trim().length > TEN_TOI_DA) loi.display_name = 'hoSoToChuc.loi.tenQuaDai';
  if (form.short_name.trim() === '') loi.short_name = 'hoSoToChuc.loi.vietTatRong';
  else if (form.short_name.trim().length > VIET_TAT_TOI_DA) loi.short_name = 'hoSoToChuc.loi.vietTatQuaDai';
  return loi;
}

export function formTuHoSo(p: Pick<TenantProfile, 'display_name' | 'short_name'>): FormHoSo {
  return { display_name: p.display_name, short_name: p.short_name };
}

/** Chỉ các trường thật sự đổi, đã cắt khoảng trắng; rỗng nghĩa là không có gì để gửi. */
export function phanThayDoi(
  goc: Pick<TenantProfile, 'display_name' | 'short_name'>,
  form: FormHoSo,
): Partial<FormHoSo> {
  const ra: Partial<FormHoSo> = {};
  const ten = form.display_name.trim();
  const viet = form.short_name.trim();
  if (ten !== goc.display_name) ra.display_name = ten;
  if (viet !== goc.short_name) ra.short_name = viet;
  return ra;
}

/** Chữ cái đầu của tên làm avatar khi tổ chức chưa có logo. */
export function chuCaiDau(ten: string): string {
  const ky = Array.from(ten.trim())[0];
  return ky ? ky.toLocaleUpperCase() : '';
}

export type MoTaDoi =
  | { kieu: 'chu'; truong: TruongDoi; truoc: string | null; sau: string | null }
  | { kieu: 'anhMoi' | 'anhDoi' | 'anhGo'; truong: TruongDoi };

/**
 * Phân một dòng diff thành kiểu hiển thị. Tên thì nêu giá trị trước và sau; ảnh
 * chỉ nêu việc đã làm, vì hash không phải thứ người dùng đọc được.
 */
export function moTaThayDoi(c: TenantProfileChange): MoTaDoi {
  const truoc = c.before ?? null;
  const sau = c.after ?? null;
  if (c.field === 'display_name' || c.field === 'short_name') {
    return { kieu: 'chu', truong: c.field, truoc, sau };
  }
  if (sau === null) return { kieu: 'anhGo', truong: c.field };
  return { kieu: truoc === null ? 'anhMoi' : 'anhDoi', truong: c.field };
}
